import ProgramBannerButton from "./ProgramBannerButton.tsx";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";
import useTermStoreMetrics from "../useTermStoreMetrics.tsx";
import { useCallback, useState } from "react";
import Confetti from "../../../../../common/core-ui/Confetti.tsx";
import usePromiseFetcher from "../../../../../common/core-ui/usePromiseFetcher.ts";
import { useStoreMultiCartFetcher } from "../../actions/storeMultiCartAction.ts";
import useToasts from "../../../../root/core-ui/toast/useToasts.ts";
import Toast from "../../../../../common/core-ui/Toast.tsx";
import { clsx } from "clsx";
import { useNavigate } from "react-router-dom";
import useVirtualStore from "../useVirtualStore.tsx";
import useInstitutionProgram from "../useInstitutionProgram.tsx";

export default function ProgramBannerAddAllButton({
  wrapperClassName,
}: {
  wrapperClassName: string;
}) {
  const intl = useIntl();
  const { isVirtualStore } = useVirtualStore();
  const { program } = useInstitutionProgram();
  const {
    requiredToPurchase,
    requiredToPurchaseCount,
    requiredToPurchaseTotalCost,
    requiredToPurchaseDefaultVariantIds,
    requiredToPurchaseInCartCount,
    needsPurchase,
    purchaseCurrency,
  } = useTermStoreMetrics();
  const { showToast } = useToasts();
  const [showConfetti, setShowConfetti] = useState(false);
  const fetcher = usePromiseFetcher<
    Awaited<Promise<ReturnType<typeof useStoreMultiCartFetcher>["data"]>>
  >({ resolveTrigger: "idle" });
  const navigate = useNavigate();

  const onClick = useCallback(() => {
    setShowConfetti(true);
    fetcher
      .submit(
        {
          variants: requiredToPurchaseDefaultVariantIds.map((id) => ({
            id,
          })),
        },
        {
          action: `/my-courses/store-multi-cart`,
          method: "POST",
          encType: "application/json",
        }
      )
      .then((response) => {
        const results = response.data;
        if (results) {
          const errors = results.filter(
            (result) => result.status === "rejected" || !result.value.ok
          );
          if (
            errors.length === requiredToPurchaseCount &&
            requiredToPurchaseCount > 3
          ) {
            // don't spam toast if something really drastic happens
            showToast(
              <Toast
                type="error"
                title={intl.formatMessage(
                  {
                    defaultMessage: "Unable to add items to cart.",
                  },
                  { count: requiredToPurchaseCount }
                )}
              />
            );
          } else {
            errors.forEach((error) => {
              const failedToCart = requiredToPurchase[results.indexOf(error)];
              const title = intl.formatMessage(
                { defaultMessage: "Could not add “{title}” to cart" },
                { title: failedToCart.title }
              );
              showToast(<Toast type={"error"} title={title} />);
            });
          }
        }
      })
      .catch(() => {});
  }, [
    fetcher,
    intl,
    requiredToPurchase,
    requiredToPurchaseCount,
    requiredToPurchaseDefaultVariantIds,
    showToast,
  ]);
  const isLoading = fetcher.state !== "idle";

  if (
    !isVirtualStore ||
    program !== "inclusive_access" ||
    (requiredToPurchaseCount === 0 && requiredToPurchaseInCartCount === 0)
  ) {
    return null;
  }

  return (
    <>
      <Confetti
        trigger={showConfetti}
        onComplete={() => setShowConfetti(false)}
      />
      <div className={clsx(wrapperClassName)}>
        {needsPurchase ? (
          <ProgramBannerButton
            key={"banner-add-all-button"}
            loading={isLoading}
            onClick={onClick}
            keepSameWidth={true}
          >
            <FormattedMessage
              defaultMessage="Add {count} to Cart ({cost})"
              values={{
                count: requiredToPurchaseCount,
                cost: (
                  <FormattedNumber
                    value={requiredToPurchaseTotalCost}
                    style="currency"
                    currency={purchaseCurrency}
                  />
                ),
              }}
            />
          </ProgramBannerButton>
        ) : (
          <ProgramBannerButton
            key={"banner-add-all-button"}
            onClick={() => {
              navigate("/my-cart");
            }}
          >
            <FormattedMessage defaultMessage="View in Cart" />
          </ProgramBannerButton>
        )}
      </div>
    </>
  );
}
